// src/pages/Spending.jsx
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { formatCurrency, formatDate } from "../lib/utils";
import { useSpendingByCategory } from "../hooks/useFinancialData";
import { cn } from "../lib/utils";

const DEMO_SPENDING = [
  { category: "Eating out", total: 380, count: 42, prev: 283 },
  { category: "Sports betting", total: 290, count: 18, prev: 330 },
  { category: "Groceries", total: 180, count: 8, prev: 196 },
  { category: "Gas / fuel", total: 120, count: 6, prev: 114 },
  { category: "Subscriptions", total: 60, count: 3, prev: 61 },
  { category: "Bars / alcohol", total: 287, count: 9, prev: 243 },
  { category: "Golf rounds", total: 137, count: 4, prev: 0 },
  { category: "Books", total: 63, count: 3, prev: 81 },
];

const COLORS = ["#D85A30","#9B7FFF","#00E5A0","#F5A623","#4D9FFF","#D4537E","#888780","#5DCAA5"];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const DEMO_MONTHLY = [
  { month: "Dec", total: 2840 },
  { month: "Jan", total: 3100 },
  { month: "Feb", total: 2760 },
  { month: "Mar", total: 3240 },
  { month: "Apr", total: 3480 },
  { month: "May", total: 3190 },
];

export function Spending() {
  const now = new Date();
  const [monthOffset, setMonthOffset] = useState(0);

  const targetDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
  const month = targetDate.getMonth();
  const year = targetDate.getFullYear();
  const monthLabel = `${MONTHS[month]} ${year}`;
  const isCurrentMonth = monthOffset === 0;

  const { spending, loading } = useSpendingByCategory(month, year);
  const displaySpending = spending.length ? spending : DEMO_SPENDING;

  const totalSpent = displaySpending.reduce((s, c) => s + c.total, 0);
  const totalPrevSpent = displaySpending.reduce((s, c) => s + (c.prev || 0), 0);
  const monthDelta = totalPrevSpent ? ((totalSpent - totalPrevSpent) / totalPrevSpent) * 100 : 0;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-lg font-bold text-text-primary">Spending</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMonthOffset((o) => o + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-border text-text-secondary hover:text-text-primary transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-[13px] font-medium text-text-primary min-w-[80px] text-center">
              {monthLabel}
            </span>
            <button
              onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
              disabled={isCurrentMonth}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-border text-text-secondary hover:text-text-primary disabled:opacity-30 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Summary card */}
        <div className="card p-4">
          <p className="font-display text-3xl font-bold text-text-primary font-tabular">
            {formatCurrency(totalSpent)}
          </p>
          <div className="flex items-center gap-2 mt-1 mb-4">
            <span className="text-[12px] text-text-tertiary">total spent</span>
            <span className={cn(
              "pill text-[10px]",
              monthDelta > 0 ? "pill-red" : "pill-green"
            )}>
              {monthDelta > 0 ? "+" : ""}{monthDelta.toFixed(1)}% vs last month
            </span>
          </div>

          {/* Three stats */}
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
            <div className="text-center">
              <p className="text-sm font-semibold text-accent-green font-tabular">+$4,380</p>
              <p className="label mt-0.5">income</p>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-sm font-semibold text-text-primary font-tabular">
                {formatCurrency(totalSpent / 4.33)}/wk
              </p>
              <p className="label mt-0.5">weekly burn</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-accent-green font-tabular">
                +{formatCurrency(4380 - totalSpent)}
              </p>
              <p className="label mt-0.5">net saved</p>
            </div>
          </div>
        </div>

        {/* Mini bar chart — last 6 months */}
        <div className="card p-4">
          <p className="label mb-3">6-month trend</p>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={DEMO_MONTHLY} barSize={24}>
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#5A5A72", fontSize: 10, fontFamily: "DM Mono" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-background-elevated border border-border rounded-xl px-3 py-2 text-xs">
                        <p className="text-text-primary font-semibold font-tabular">
                          {formatCurrency(payload[0].value)}
                        </p>
                      </div>
                    );
                  }}
                  cursor={false}
                />
                <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                  {DEMO_MONTHLY.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={i === DEMO_MONTHLY.length - 1 ? "#00E5A0" : "#1C1C28"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category breakdown */}
        <div>
          <p className="label px-1 mb-2">By category</p>
          <div className="card p-0 overflow-hidden divide-y divide-border">
            {displaySpending.map((item, idx) => {
              const prev = item.prev || 0;
              const pctChange = prev ? ((item.total - prev) / prev) * 100 : null;
              const isNew = prev === 0;

              return (
                <div key={item.category} className="flex items-center gap-3 px-4 py-3">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-text-primary">{item.category}</p>
                    <p className="text-[11px] text-text-tertiary mt-0.5">{item.count} transactions</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[14px] font-semibold text-text-primary font-tabular">
                      {formatCurrency(item.total)}
                    </p>
                    <div className="flex justify-end mt-0.5">
                      {isNew ? (
                        <span className="pill pill-blue text-[10px]">new</span>
                      ) : pctChange !== null ? (
                        <span className={cn("pill text-[10px]", pctChange > 0 ? "pill-red" : "pill-green")}>
                          {pctChange > 0 ? "+" : ""}{pctChange.toFixed(1)}%
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
